import { useCallback, useState, type FC } from 'react';
import { BsCheckCircleFill, BsXCircleFill } from 'react-icons/bs';
import { IoCheckmark } from 'react-icons/io5';

import { type Option, type Field } from '@core/blocks/edu-flow-2/components/Fields/types';
import useRoundFieldsTest from '@core/hooks/RoundFieldsTest/useRoundFieldsTest';
import useEmbeddedVoyagerObserver from '@core/hooks/useEmbeddedVoyagerObserver';
import { type VoyagerInput } from '@core/reducers/inputsSlice';
import cn from '@core/utils/classNames';
import convertToSlug from '@core/utils/strings/convertToSlug';

import styles from './OptionGroupOptions.module.css';

const iconMap = {
  yes: <BsCheckCircleFill />,
  no: <BsXCircleFill />,
};

type Props = {
  field: Field;
  handleSelection: (option: Option) => void;
  fieldInputObject?: VoyagerInput;
  isSelected: (selection: VoyagerInput, option: Option) => boolean;
};

const OptionGroupOptions: FC<Props> = ({ field, handleSelection, fieldInputObject, isSelected }) => {
  const findIndex = useCallback(
    (selection?: VoyagerInput, option?: Option) =>
      selection?.options?.findIndex((selectedOption: Option) => selectedOption.label === option?.label),
    []
  );
  const { fullRounded, xlRounded } = useRoundFieldsTest();
  const { isEmbeddedVoyager } = useEmbeddedVoyagerObserver();
  return (
    <div data-testid="option-group" className={cn(styles.checkboxGroup, isEmbeddedVoyager && styles.embeddedWidth)}>
      {(field.options as Option[])?.map((value, index) =>
        field.mode !== 'yesorno' ? (
          <label
            key={index}
            onChange={() => {
              handleSelection(value);
            }}
            htmlFor={`option-${index}`}
            className={cn(
              styles.checkboxContainer,
              isEmbeddedVoyager && styles.embeddedContainerText,
              isSelected(fieldInputObject as VoyagerInput, value) && styles.selected,
              fullRounded
            )}
            data-testid={convertToSlug(`${field.name} ${value.value}`)}
          >
            {field.mode === 'incrementCheckbox' && isSelected(fieldInputObject as VoyagerInput, value) ? (
              <div className={styles.number}> {findIndex(fieldInputObject as VoyagerInput, value) + 1}.</div>
            ) : (
              <IoCheckmark />
            )}
            {value.label}
            <input id={`option-${index}`} type="checkbox" className={styles.hiddenCheckbox} />
          </label>
        ) : (
          <label
            key={value.label}
            onChange={() => handleSelection(value)}
            className={cn(
              styles.yesOrNo,
              isSelected(fieldInputObject as VoyagerInput, value) && styles.selected,
              xlRounded
            )}
            htmlFor={value.label}
            data-testid={convertToSlug(`${field.name} ${value.value}`)}
          >
            <input id={value.label} type="checkbox" className={styles.hiddenCheckbox} />
            <div className={styles.labelContent}>
              {iconMap[value.icon as keyof typeof iconMap]}
              <span className={styles.labelText}>{value.label}</span>
            </div>
          </label>
        )
      )}
    </div>
  );
};

export default OptionGroupOptions;
